
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { format, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import { ExpenseFileUploadField } from "@/components/expenses/ExpenseFileUploadField";

interface PlotExpenseFormProps {
  plotId: string;
  onExpenseCreated: () => void;
  onClose: () => void;
}

export const PlotExpenseForm = ({ plotId, onExpenseCreated, onClose }: PlotExpenseFormProps) => {
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  // Fetch categories from the database
  const { data: categories = [] } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('expense_categories')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching categories:', error);
        throw error;
      }

      return data;
    },
  });

  // Get month options for the last 12 months
  const getMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      options.push({
        value: format(date, "yyyy-MM"),
        label: format(date, "MMMM yyyy", { locale: es })
      });
    }
    
    return options;
  };

  // Get last day of selected month
  const getLastDayOfMonth = (yearMonth: string) => {
    const [year, month] = yearMonth.split("-").map(Number);
    return endOfMonth(new Date(year, month - 1));
  };

  const uploadReceipt = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `plot-expenses/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('expense-receipts')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Error uploading receipt:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('expense-receipts')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error in uploadReceipt:', error);
      return null;
    }
  };

  const handleCreateExpense = async () => {
    if (!amount || !category || !description || !selectedMonth) {
      toast({
        title: "Error",
        description: "Por favor complete todos los campos requeridos",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      // Get the last day of the selected month for expense_date
      const expenseDate = format(getLastDayOfMonth(selectedMonth), "yyyy-MM-dd");
      
      // Upload receipt file if present
      let receiptUrl = null;
      if (receiptFile) {
        receiptUrl = await uploadReceipt(receiptFile);
      }

      // Create the expense record
      const { error } = await supabase.from("expenses").insert({
        entity_id: plotId,
        entity_type: 'plot',
        amount: parseInt(amount),
        category,
        description,
        expense_date: expenseDate,
        receipt_url: receiptUrl,
      });

      if (error) {
        console.error("Error creating plot expense:", error);
        toast({
          title: "Error",
          description: "No se pudo crear el gasto de la parcela",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: "Gasto de parcela creado exitosamente",
      });

      onClose();
      setAmount("");
      setCategory("");
      setDescription("");
      setSelectedMonth(format(new Date(), "yyyy-MM"));
      setReceiptFile(null);
      onExpenseCreated();
    } catch (error) {
      console.error("Error in handleCreateExpense:", error);
      toast({
        title: "Error",
        description: "Ocurrió un error al crear el gasto",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label htmlFor="amount" className="text-base">Monto</Label>
        <Input
          id="amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Ingrese monto"
          className="text-base"
          required
          disabled={isUploading}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="category" className="text-base">Categoría</Label>
        <Select value={category} onValueChange={setCategory} disabled={isUploading} required>
          <SelectTrigger className="bg-white">
            <SelectValue placeholder="Seleccione categoría" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="month" className="text-base">Mes</Label>
        <Select value={selectedMonth} onValueChange={setSelectedMonth} disabled={isUploading} required>
          <SelectTrigger id="month" className="bg-white">
            <SelectValue placeholder="Seleccione mes" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {getMonthOptions().map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Fecha seleccionada: {format(getLastDayOfMonth(selectedMonth), "dd/MM/yyyy", { locale: es })}
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description" className="text-base">Descripción</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ingrese descripción"
          className="text-base"
          required
          disabled={isUploading}
        />
      </div>

      <ExpenseFileUploadField 
        onFileChange={setReceiptFile} 
        isUploading={isUploading} 
      />

      <button 
        onClick={handleCreateExpense} 
        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isUploading}
      >
        {isUploading ? "Creando..." : "Crear Gasto"}
      </button>
    </div>
  );
};
